import { Layout } from '@components/Layout';
import { NestedSidebar } from '@components/SidebarV2';
import type { NestedSidebarItem } from '@components/SidebarV2';
import PermissionGate from '@components/upercut-permissions/PermissionGate';
import JobOfferAddModule from '@modules/JobOffers/JobOfferAdd';
import JobOfferApplicationModule from '@modules/JobOffers/JobOfferApplication';
import JobOfferApplicationStats from '@modules/JobOffers/JobOfferApplicationStats';
import JobOfferDetail from '@modules/JobOffers/JobOfferDetail/JobOfferDetail';
import JobOfferEditModule from '@modules/JobOffers/JobOfferEdit';
import JobOfferListModule from '@modules/JobOffers/JobOfferManagement';
import JobOfferScoringModule from '@modules/JobOffers/JobOfferScoring';
import CardJobOfferHeading from '@modules/JobOffers/components/JobOfferHeading';
import ProfileSkeleton from '@modules/talents/components/ProfileSkeleton';
import { AIIcon } from '@modules/scoring/components/ScoringScoreBadge';
import { Box } from '@mui/material';
import { Stack } from '@mui/system';
import { useQuery } from '@apollo/client';
import { GET_JOB_OFFER } from '@gql/jobs';
import { useMemo } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';

const JobOfferPage = () => {
    return (
        <Routes>
            <Route element={<JobOfferListLayout />}>
                <Route
                    path="/"
                    element={
                        <PermissionGate permission="job-offers:read">
                            <JobOfferListModule />
                        </PermissionGate>
                    }
                />
                <Route
                    path="/add"
                    element={
                        <PermissionGate permission="job-offers:create">
                            <JobOfferAddModule />
                        </PermissionGate>
                    }
                />
                <Route
                    path="/:slug"
                    element={<JobOfferDetailsLayout />}
                >
                    <Route
                        index
                        element={<Navigate to="detail" replace />}
                    />
                    <Route
                        path="detail"
                        element={
                            <PermissionGate permission="job-offers:read">
                                <JobOfferDetail />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="applications"
                        element={
                            <PermissionGate permission="job-applications:read">
                                <JobOfferApplicationModule />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="stats"
                        element={
                            <PermissionGate permission="job-applications:read">
                                <JobOfferApplicationStats />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="scoring"
                        element={
                            <PermissionGate permission="scoring:read">
                                <JobOfferScoringModule />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="add"
                        element={
                            <PermissionGate permission="job-offers:create">
                                <JobOfferAddModule />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="edit"
                        element={
                            <PermissionGate permission="job-offers:update">
                                <JobOfferEditModule />
                            </PermissionGate>
                        }
                    />
                </Route>
            </Route>
            <Route
                path="/templates"
                element={
                    <PermissionGate permission="job-offers:read">
                        <h1>Under Construction</h1>
                    </PermissionGate>
                }
            />
            <Route
                path="/templates/create"
                element={
                    <PermissionGate permission="job-offers:create">
                        <h1>Under Construction</h1>
                    </PermissionGate>
                }
            />
            <Route
                path="/:slug/*"
                element={<JobOfferSlugFallbackRedirect />}
            />
        </Routes>
    );
};

const JobOfferListLayout = () => {
    return (
        <Layout
            breadcrumbs={[
                { label: 'Tableau de bord', href: '/', value: 'dashboard' },
                { label: 'Liste des offres d\'emploi', href: '/job-offers', value: 'job-offers' },
                { label: 'Créer une offre d\'emploi', href: '/job-offers/:slug/add', value: 'add' },
                { label: 'Candidatures', href: '/job-offers/:slug/applications', value: 'applications' },
                { label: 'Détails de l\'offre d\'emploi', href: '/job-offers/:slug/detail', value: 'detail' },
                { label: 'Modifier une offre d\'emploi', href: '/job-offers/:slug/edit', value: 'edit' },
                { label: 'Modèles d\'offre d\'emploi', href: '/job-offers/templates', value: 'template' },
                { label: 'Statistiques des candidatures', href: '/job-offers/:slug/stats', value: 'stats' },
                { label: 'Scoring', href: '/job-offers/:slug/scoring', value: 'scoring' },
            ]}
            heading="Gestion des offres d'emploi"
        >
            <Outlet />
        </Layout>
    );
};

const JobOfferSlugFallbackRedirect = () => {
    const { slug } = useParams<{ slug: string }>();

    if (!slug) {
        return <Navigate to="/job-offers" replace />;
    }

    return <Navigate to={`/job-offers/${slug}/detail`} replace />;
};

const JobOfferDetailsLayout = () => {
    const { slug } = useParams<{ slug: string }>();

    const { data: jobOfferData, loading: loadingJobOfferData } = useQuery(GET_JOB_OFFER, {
        variables: { jobNumber: slug, showInactive: true },
        skip: !slug,
    });

    const location = useLocation()

    const isDetailPage = location.pathname.endsWith('/detail') || location.pathname === `/job-offers/${slug}`;

    const jobOfferDetailData = useMemo(() => jobOfferData?.getJobOffer, [jobOfferData]);

    const nestedItems: NestedSidebarItem[] = useMemo(
        () => [
            { id: 'detail', label: 'Info', icon: 'fi-rr-info', path: `/job-offers/${slug}/detail` },
            { id: 'applications', label: 'Archive', icon: 'fi-br-inbox', path: `/job-offers/${slug}/applications` },
            { id: 'stats', label: 'Stats', icon: 'fi-rr-chart-histogram', path: `/job-offers/${slug}/stats` },
            { id: 'scoring', label: 'Scoring', icon: <AIIcon />, path: `/job-offers/${slug}/scoring` },
        ],
        [slug],
    );

    if (loadingJobOfferData) {
        return (
            <Stack sx={{ pt: 0, flex: 1, minHeight: '100%', gap: 3 }}>
                <ProfileSkeleton />
            </Stack>
        );
    }

    return (
        <Box sx={{ display: 'flex', flex: 1, minHeight: '100%', mx: '-24px' }}>
            <NestedSidebar items={nestedItems} title="Offre d'emploi" />
            <Stack flex={1} width={0} pt={0} minHeight="100%">
                {!isDetailPage && <Stack sx={{ gap: 3, mb: 3, px: 3 }}>
                    <CardJobOfferHeading
                        jobOfferId={jobOfferDetailData?._id}
                        jobNumber={jobOfferDetailData?.jobNumber}
                        onConfirm={() => {}}
                    />
                </Stack>}
                <Box sx={{ flex: 1, pb: 3, display: 'flex', px: '24px', minWidth: 0 }}>
                    <Outlet />
                </Box>
            </Stack>
        </Box>
    );
};

export default JobOfferPage;
